// /admin/v2/reports — the daily report archive (BRIEFS C3.4, SPEC §7 "Report archive/resend").
// List of stored reports (filter by student), open one in a frame, preview any day without
// sending, and send (or send again) a day's report by email. Endpoints from C3.3.
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  errText, fmtDateTime, istToday, v2ReportHtml, v2ReportPreview, v2ReportSend, v2Reports,
} from '../../../api/v2admin';
import V2Tabs, { Msg } from './V2Tabs';
import StudentPicker from './StudentPicker';

function Frame({ shown, onClose }) {
  if (!shown) return null;
  return (
    <div className="card" style={{ padding: 0, marginBottom: 18, overflow: 'hidden' }} data-testid="v2rep-frame">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
        <strong style={{ flex: 1 }}>{shown.title}</strong>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
      </div>
      <iframe title={shown.title} srcDoc={shown.html} sandbox=""
        style={{ width: '100%', height: 720, border: 0, background: 'white', display: 'block' }} />
    </div>
  );
}

export default function AdminV2Reports() {
  const [params, setParams] = useSearchParams();
  const student = params.get('student') || '';
  const [day, setDay] = useState(istToday());
  const [shown, setShown] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const listQ = useQuery({ queryKey: ['v2a', 'reports', student], queryFn: () => v2Reports(student || undefined) });
  const reports = (listQ.data && listQ.data.reports) || [];

  const open = async (r) => {
    setMsg(null);
    try {
      const html = await v2ReportHtml(r.id);
      setShown({ title: `${r.student_name} — ${r.report_date} (as stored)`, html });
    } catch (x) { setMsg({ kind: 'err', text: errText(x, 'Could not open the report.') }); }
  };
  const preview = async () => {
    if (!student) { setMsg({ kind: 'err', text: 'Choose a student first.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const html = await v2ReportPreview(student, day);
      setShown({ title: `Preview — ${day} (not sent)`, html });
    } catch (x) { setMsg({ kind: 'err', text: errText(x, 'Could not make the preview.') }); }
    finally { setBusy(false); }
  };
  const send = async (studentId, d, name) => {
    if (!studentId) { setMsg({ kind: 'err', text: 'Choose a student first.' }); return; }
    if (!window.confirm(`Send the report for ${name || 'this student'}, ${d}, by email now?`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = await v2ReportSend(studentId, d);
      setMsg(r.email_sent
        ? { kind: 'ok', text: `Email sent to ${r.to}.` }
        : { kind: 'err', text: `The email was not sent: ${r.error || 'unknown problem'}. The report is saved; you can try again.` });
      listQ.refetch();
    } catch (x) { setMsg({ kind: 'err', text: errText(x, 'Could not send the report.') }); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <V2Tabs group="student" title="Daily reports" intro="Every daily report the app made. Open one to see the email as it was sent. Preview shows any day without sending; Send emails it now (also to send again)." />
      <Msg msg={msg} />
      <div className="card" style={{ padding: '14px 18px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <StudentPicker value={student} allowEmpty testId="v2rep-student"
            onChange={(id) => setParams(id ? { student: id } : {})} />
          <input type="date" className="form-input" value={day} max={istToday()} data-testid="v2rep-day"
            onChange={(e) => e.target.value && setDay(e.target.value)} style={{ maxWidth: 170 }} />
          <button className="btn btn-ghost" disabled={busy || !student} onClick={preview} data-testid="v2rep-preview">Preview this day</button>
          <button className="btn btn-primary" disabled={busy || !student} onClick={() => send(student, day)} data-testid="v2rep-send">Send this day now</button>
        </div>
        {!student && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>Choose a student to preview or send a day.</div>}
      </div>
      <Frame shown={shown} onClose={() => setShown(null)} />
      {listQ.isLoading ? <div>Loading…</div> : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }} data-testid="v2rep-list">
            <thead><tr><th>Day</th><th>Student</th><th>Questions</th><th>Study time</th><th>Email</th><th /></tr></thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.report_date}</td>
                  <td>{r.student_name}</td>
                  <td>{r.questions}</td>
                  <td>{r.active_minutes} min</td>
                  <td style={{ fontSize: 13 }}>
                    {r.email_sent
                      ? <><span className="badge badge-green">Sent</span> <span style={{ color: 'var(--text-muted)' }}>{fmtDateTime(r.sent_at)}</span></>
                      : <><span className="badge badge-red">Not sent</span> <span style={{ color: 'var(--text-muted)' }}>{r.error || ''}</span></>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => open(r)} data-testid={`v2rep-open-${r.id}`}>Open</button>{' '}
                    <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => send(r.student_id, r.report_date, r.student_name)}>
                      {r.email_sent ? 'Send again' : 'Send'}
                    </button>
                  </td>
                </tr>
              ))}
              {!reports.length && <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>No reports yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
